/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  Modal,
  ModalDialog,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/joy";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useMembershipStore from "../stores/membershipStore";
import { useModelStore, type ModelType } from "../stores/modelStore";
import { BetaBadge } from "./BetaBadge";
import {
  AdvancedRequiredChip,
  AdvancedRequiredModal,
  PremiumRequiredChip,
  PremiumRequiredModal,
} from "./PremiumRequired";

interface ModelSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export function ModelSettingsModal({
  open,
  onClose,
}: ModelSettingsModalProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Model selection state
  const selectedModel = useModelStore((state) => state.selectedModel);
  const setSelectedModel = useModelStore((state) => state.setSelectedModel);
  const isPremium = useMembershipStore((state) => state.isPremium);
  const isAdvancedOrHigher = useMembershipStore(
    (state) => state.isAdvancedOrHigher,
  );
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  const [showAdvancedPrompt, setShowAdvancedPrompt] = useState(false);

  const isPremiumOnlyModel = (model: ModelType) =>
    model === "deepseek" || model === "mistral";

  const isAdvancedOnlyModel = (model: ModelType) =>
    model === "claude" ||
    model === "wizardlm" ||
    model === "hermes" ||
    model === "llama8b" ||
    model === "lunaris" ||
    model === "llama70b" ||
    model === "gemini" ||
    model === "glm";

  const handleModelChange = (value: ModelType) => {
    if (isAdvancedOnlyModel(value) && !isAdvancedOrHigher()) {
      setShowAdvancedPrompt(true);
      return;
    }
    if (!isPremium && isPremiumOnlyModel(value)) {
      setShowPremiumPrompt(true);
      return;
    }
    setSelectedModel(value);
  };

  const handleViewMembership = () => {
    setShowPremiumPrompt(false);
    navigate("/membership");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "100vw", sm: "min(90vw, 500px)" },
          maxWidth: { xs: "100vw", sm: 500 },
          height: { xs: "100dvh", sm: "auto" },
          maxHeight: { xs: "100dvh", sm: "90vh" },
          m: { xs: 0, sm: 2 },
          px: 2.5,
          pt: { xs: "max(12px, env(safe-area-inset-top))", sm: 2.5 },
          pb: { xs: "max(12px, env(safe-area-inset-bottom))", sm: 2.5 },
          borderRadius: { xs: 0, sm: "md" },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography level="title-lg" sx={{ flex: 1, fontSize: "1.1rem" }}>
              {t("modelSettings.title")}
            </Typography>
            <IconButton onClick={onClose} size="sm" sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
            {t("modelSettings.description")}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Box>
            <FormControl>
              <RadioGroup
                value={selectedModel}
                onChange={(e) => handleModelChange(e.target.value as ModelType)}
                sx={{ gap: 1 }}
              >
                <Radio
                  value="recommended"
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.recommended")}
                        </Typography>
                        <Chip
                          size="sm"
                          variant="solid"
                          color="neutral"
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            px: 0.75,
                            py: 0.25,
                          }}
                        >
                          {t("modelSelector.recommendedBadge")}
                        </Chip>
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.recommendedDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="povchat"
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.roleplay")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          {[...Array(5)].map((_, i) => (
                            <Typography
                              key={i}
                              sx={{
                                fontSize: "0.8rem",
                                color: "#7b1fa2",
                                lineHeight: 1,
                              }}
                            >
                              ★
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.roleplayDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="chatgpt"
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.chatgpt")}
                        </Typography>
                        <Chip
                          size="sm"
                          variant="soft"
                          color="success"
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            px: 0.75,
                            py: 0.25,
                          }}
                        >
                          SFW
                        </Chip>
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.chatgptDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="deepseek"
                  sx={{
                    opacity:
                      !isPremium && isPremiumOnlyModel("deepseek") ? 0.55 : 1,
                    cursor:
                      !isPremium && isPremiumOnlyModel("deepseek")
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={() => {
                    if (!isPremium) {
                      setShowPremiumPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.deepseek")}
                        </Typography>
                        <PremiumRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.deepseekDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="mistral"
                  sx={{
                    opacity:
                      !isPremium && isPremiumOnlyModel("mistral") ? 0.55 : 1,
                    cursor:
                      !isPremium && isPremiumOnlyModel("mistral")
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={() => {
                    if (!isPremium) {
                      setShowPremiumPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.mistral")}
                        </Typography>
                        <PremiumRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.mistralDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="wizardlm"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.wizardlm")}
                        </Typography>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.wizardlmDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="hermes"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.hermes")}
                        </Typography>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.hermesDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="llama8b"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.llama8b")}
                        </Typography>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.llama8bDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="lunaris"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.lunaris")}
                        </Typography>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.lunarisDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="llama70b"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.llama70b")}
                        </Typography>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.llama70bDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="gemini"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.gemini")}
                        </Typography>
                        <Chip
                          size="sm"
                          variant="soft"
                          color="success"
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            px: 0.75,
                            py: 0.25,
                          }}
                        >
                          SFW
                        </Chip>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.geminiDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="glm"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.glm")}
                        </Typography>
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.glmDescription")}
                      </Typography>
                    </Box>
                  }
                />
                <Radio
                  value="claude"
                  sx={{
                    opacity: !isAdvancedOrHigher() ? 0.55 : 1,
                    cursor: !isAdvancedOrHigher() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!isAdvancedOrHigher()) {
                      setShowAdvancedPrompt(true);
                    }
                  }}
                  label={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography level="title-sm">
                          {t("modelSelector.claude")}
                        </Typography>
                        <BetaBadge />
                        <AdvancedRequiredChip size="sm" />
                      </Box>
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("modelSelector.claudeDescription")}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        <PremiumRequiredModal
          open={showPremiumPrompt}
          onClose={() => setShowPremiumPrompt(false)}
          onViewMembership={handleViewMembership}
        />

        <AdvancedRequiredModal
          open={showAdvancedPrompt}
          onClose={() => setShowAdvancedPrompt(false)}
          onViewMembership={handleViewMembership}
        />
      </ModalDialog>
    </Modal>
  );
}
